import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Skeleton } from "antd";
import ReactECharts from "echarts-for-react";

import SingleDeviceHeader from "../SingleDeviceHeader";
import ViewDeviceExplorerHeader from "../Explorer/common/header";
import ViewDeviceExplorerAttribute from "../Explorer/common/attributes";
import ViewDeviceExplorerFilter from "../Explorer/common/filter";
import { useDispatch, useSelector } from "react-redux";

import explorer_graph_option from "../Explorer/graph-config/explorer_graph";
import { fetchDeviceRequest } from "../../../store/actions/singleDeviceHeader";
import { fetchDeviceExplorerGraphRequest } from "../../../store/actions/deviceExplorer";

// Converts camelCase / underscore keys to Title Case as a last-resort fallback
const toFriendlyLabel = (key) =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// 16 perceptually distinct colors — enough to cover all attributes without repeating
const CHART_COLORS = [
  "#FF8027", // orange
  "#0089EC", // blue
  "#63D48E", // green
  "#D52B1E", // red
  "#A855F7", // purple
  "#F59E0B", // amber
  "#06B6D4", // cyan
  "#EC4899", // pink
  "#84CC16", // lime
  "#6366F1", // indigo
  "#14B8A6", // teal
  "#F97316", // deep orange
  "#8B5CF6", // violet
  "#EF4444", // rose
  "#10B981", // emerald
  "#3B82F6", // sky blue
];

function ViewDeviceExplorer() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const isDeviceDataLoading = useSelector(
    (state) => state.singleDeviceHeader.isLoading
  );
  const isLoading = useSelector(
    (state) => state.deviceExplorer.isLoading
  );
  const isBLE = useSelector((state) => state.singleDeviceHeader.isBLE);
  const allAttributes = useSelector((state) =>
    state.singleDeviceHeader.isBLE
      ? state.singleDeviceHeader.bleAttributes
      : state.singleDeviceHeader.gatewayAttributes
  );
  const data = useSelector((state) => state.deviceExplorer.data);
  const aggregation = useSelector((state) => state.deviceExplorer.selectedAggregation);
  const seletedAttributes = useSelector((state) => state.deviceExplorer.seletedAttributes);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    dispatch(fetchDeviceRequest({ id }));
  }, [dispatch, id, user?.accountId]);

  useEffect(() => {
    if (isBLE === null || allAttributes.length === 0) return;
    const firstAttribute = allAttributes[0]?.value;
    dispatch(fetchDeviceExplorerGraphRequest({ id, isBLE, firstAttribute }));
  }, [dispatch, id, isBLE, allAttributes]);
  const hasData = Object.keys(data).length > 0;

  const series = seletedAttributes.map((attrValue, i) => {
    const schema = allAttributes.find(a => a.value === attrValue);
    const colorIndex = allAttributes.findIndex(a => a.value === attrValue);
    const seriesData = data[attrValue]?.[aggregation] ?? [];
    return {
      name: toFriendlyLabel(schema?.label ?? attrValue),
      type: "line",
      yAxisIndex: i === 0 ? 0 : 1,
      data: seriesData,
      symbol: "none",
      itemStyle: { color: CHART_COLORS[colorIndex % CHART_COLORS.length] },
    };
  });

  const yAxis = [
    {
      type: "value",
      name: toFriendlyLabel(allAttributes.find(a => a.value === seletedAttributes[0])?.label ?? seletedAttributes[0] ?? ""),
      nameGap: 40,
      nameRotate: 90,
      nameLocation: "middle",
    },
    {
      type: "value",
      name: toFriendlyLabel(allAttributes.find(a => a.value === seletedAttributes[1])?.label ?? seletedAttributes[1] ?? ""),
      nameGap: 45,
      nameRotate: -90,
      nameLocation: "middle",
    },
  ];

  return (
    <div className="view-device-dashboard-wrapper">
      {isDeviceDataLoading && <Skeleton paragraph={{ rows: 2 }} />}
      {!isDeviceDataLoading && <SingleDeviceHeader isBLE={isBLE} />}
      <div className="view-device-dashboard-full">
        <ViewDeviceExplorerHeader id={id} />
        <ViewDeviceExplorerAttribute isBLE={isBLE} />
        <ViewDeviceExplorerFilter id={id} />
        <div className="pt-4">
          {seletedAttributes.length > 0 && !isLoading && !hasData && (
            <div style={{ height: "451px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ marginTop: "8px", fontSize: "14px", fontWeight: "500" }}>
                No data available for the selected time range, Try adjusting the filters.
              </span>
            </div>
          )}
          {seletedAttributes.length > 0 && (isLoading || hasData) && <ReactECharts
            style={{ height: "451px" }}
            notMerge={true}
            showLoading={isLoading}
            option={{
              ...explorer_graph_option,
              legend: {
                ...explorer_graph_option.legend,
                data: seletedAttributes.map(attrValue => {
                  const schema = allAttributes.find(a => a.value === attrValue);
                  return toFriendlyLabel(schema?.label ?? attrValue);
                }),
              },
              series,
              yAxis,
            }}
          />}
        </div>
      </div>
    </div>
  );
}

export default ViewDeviceExplorer;
